import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { createProviderSchema, type CreateProviderInput } from "@/lib/schemas/ai-schemas";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";

interface CreateProviderDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: CreateProviderInput) => void;
  isPending: boolean;
}

export function CreateProviderDialog({
  isOpen,
  onClose,
  onSubmit,
  isPending,
}: CreateProviderDialogProps) {
  const form = useForm<z.input<typeof createProviderSchema>, unknown, CreateProviderInput>({
    resolver: zodResolver(createProviderSchema),
    defaultValues: { id: "", name: "", adapterType: "", apiKey: "", baseUrl: "" },
  });

  function handleClose() {
    form.reset();
    onClose();
  }

  function handleSubmit(data: CreateProviderInput) {
    onSubmit(data);
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent data-testid="create-dialog">
        <DialogHeader>
          <DialogTitle>Add Provider</DialogTitle>
          <DialogDescription>Add a new AI provider configuration.</DialogDescription>
        </DialogHeader>
        <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="cp-id">ID</Label>
              <Input
                id="cp-id"
                placeholder="openai"
                {...form.register("id")}
                data-testid="create-id"
              />
              {form.formState.errors.id && (
                <p className="text-sm text-red-600">{form.formState.errors.id.message}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="cp-name">Name</Label>
              <Input
                id="cp-name"
                placeholder="OpenAI"
                {...form.register("name")}
                data-testid="create-name"
              />
              {form.formState.errors.name && (
                <p className="text-sm text-red-600">{form.formState.errors.name.message}</p>
              )}
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="cp-adapter">Adapter Type</Label>
            <Input
              id="cp-adapter"
              placeholder="openai"
              {...form.register("adapterType")}
              data-testid="create-adapter"
            />
            {form.formState.errors.adapterType && (
              <p className="text-sm text-red-600">{form.formState.errors.adapterType.message}</p>
            )}
          </div>
          <div className="space-y-2">
            <Label htmlFor="cp-key">API Key</Label>
            <Input
              id="cp-key"
              type="password"
              placeholder="sk-..."
              {...form.register("apiKey")}
              data-testid="create-key"
            />
            {form.formState.errors.apiKey && (
              <p className="text-sm text-red-600">{form.formState.errors.apiKey.message}</p>
            )}
          </div>
          <div className="space-y-2">
            <Label htmlFor="cp-url">Base URL</Label>
            <Input
              id="cp-url"
              placeholder="https://api.openai.com/v1"
              {...form.register("baseUrl")}
              data-testid="create-url"
            />
            {form.formState.errors.baseUrl && (
              <p className="text-sm text-red-600">{form.formState.errors.baseUrl.message}</p>
            )}
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="cp-rpm">RPM Limit (optional)</Label>
              <Input
                id="cp-rpm"
                type="number"
                {...form.register("rpmLimit")}
                data-testid="create-rpm"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="cp-rps">RPS Limit (optional)</Label>
              <Input
                id="cp-rps"
                type="number"
                {...form.register("rpsLimit")}
                data-testid="create-rps"
              />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={handleClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={isPending}>
              {isPending ? "Creating..." : "Create"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
